import {
  Injectable,
  OnDestroy,
  Optional,
} from '@angular/core';
import {
  Event,
  NavigationEnd,
  Router,
} from '@angular/router';
import {
  FactsService,
  RulesEngineRunnerService,
} from '@o3r/rules-engine';
import {
  AnalyticsService,
} from '@refx/analytics-core';
import {
  BehaviorSubject,
  Subscription,
} from 'rxjs';
import {
  filter,
  shareReplay,
} from 'rxjs/operators';
import {
  retrieveCurrentDate,
  retrieveCurrentDayOfTheWeek,
  retrieveCurrentHour,
  retrieveCurrentMinute,
  retrieveCurrentTimestamp,
  retrieveCurrentUTCDayOfTheWeek,
  retrieveCurrentUTCHour,
  retrieveCurrentUTCMinute,
  retrieveCurrentUTCTimestamp,
  retrieveDeviceType,
  retrieveLocationHref,
  retrieveLocationOrigin,
  retrieveLocationPathname,
  retrieveOperatingSystem,
  retrieveOperatingSystemVersion,
} from './fact-factories';
import {
  TechnicalDetailsFacts,
} from './technical-details.facts';

@Injectable()
export class TechnicalDetailsFactsService extends FactsService<TechnicalDetailsFacts> implements OnDestroy {
  private readonly currentDate = new Date();

  // Router events are not replayed by default, so we need to replay them ourselves
  // Only navigationEnd events are interesting for us (and limit the rate of events to keep)
  private readonly navigationEndEventsSubject$: BehaviorSubject<Event | null> = new BehaviorSubject<Event | null>(null);
  private readonly navigationEndEvents$ = this.navigationEndEventsSubject$.asObservable().pipe(shareReplay({
    refCount: true,
    bufferSize: 1
  }));

  protected subscriptions: Subscription[] = [];

  public facts = {
    currentHour: retrieveCurrentHour()(this.currentDate),
    currentUTCHour: retrieveCurrentUTCHour()(this.currentDate),
    currentMinute: retrieveCurrentMinute()(this.currentDate),
    currentUTCMinute: retrieveCurrentUTCMinute()(this.currentDate),
    currentDate: retrieveCurrentDate()(this.currentDate),
    currentTimestamp: retrieveCurrentTimestamp()(this.currentDate),
    currentUTCTimestamp: retrieveCurrentUTCTimestamp()(this.currentDate),
    currentDayOfTheWeek: retrieveCurrentDayOfTheWeek()(this.currentDate),
    currentUTCDayOfTheWeek: retrieveCurrentUTCDayOfTheWeek()(this.currentDate),
    operatingSystem: retrieveOperatingSystem()(this.analyticsService),
    operatingSystemVersion: retrieveOperatingSystemVersion()(this.analyticsService),
    deviceType: retrieveDeviceType()(this.analyticsService),
    locationHref: this.navigationEndEvents$.pipe(retrieveLocationHref()),
    locationOrigin: this.navigationEndEvents$.pipe(retrieveLocationOrigin()),
    locationPathname: this.navigationEndEvents$.pipe(retrieveLocationPathname())
  };

  constructor(
    rulesEngine: RulesEngineRunnerService,
    private readonly router: Router,
    @Optional() private readonly analyticsService?: AnalyticsService
  ) {
    super(rulesEngine);

    this.subscriptions.push(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      ).subscribe((event) => this.navigationEndEventsSubject$.next(event))
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
